// src/contexts/AccessProvider.js
import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import { UserProfileContext } from './UserProfileContext';
import { validateAccess, fetchAssetByAccessKey } from '../services/api/assets';
import { isValidAccessKey } from '../utils/accessKeyUtils';
import {
  appendAccessHistory,
  slimAccessForPersist,
} from '../utils/accessStorage';
import { clearTaskNamespace } from '../utils/taskStorage';
import { API_URL } from '../../config/env';

export const AccessContext = createContext(null);

const FIVE_MINUTES = 5 * 60 * 1000;

/** Normalize asset JSON from GET access_key so tasks include string access_key and flat expiry. */
export function normalizeAssetForAccessKey(access_key, assetData) {
  if (!assetData || typeof assetData !== 'object') return assetData;
  const out = { ...assetData };
  const tasksWithAccessKey = {};
  Object.keys(out.tasks || {}).forEach((taskId) => {
    const task = out.tasks[taskId];
    tasksWithAccessKey[taskId] = {
      ...task,
      access_key: task.access_key?.key || task.access_key || access_key,
      access_duration_until: task.access_key?.expires_at || task.access_duration_until,
    };
  });
  out.tasks = tasksWithAccessKey;
  return out;
}

function sweepCompletedTaskStorage(access_key, assetData) {
  if (!access_key || !assetData) return;

  Object.entries(assetData.tasks || {}).forEach(([taskId, task]) => {
    if (task?.completed_at) {
      clearTaskNamespace(access_key, taskId);
    }
  });

  (assetData.task_history || []).forEach((task) => {
    if (task?.id != null) {
      clearTaskNamespace(access_key, String(task.id));
    }
  });
}

export function AccessProvider({ children }) {
  const { userProfile, updateUserProfile } = useContext(UserProfileContext);
  // userProfile.accesses = { [access_key]: { access_key, ... } }

  const [assetMap, setAssetMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const assetMapRef = useRef(assetMap);
  assetMapRef.current = assetMap;

  const userProfileRef = useRef(userProfile);
  userProfileRef.current = userProfile;

  const fetchAndValidateAccess = useCallback(async (access_key) => {
    if (!isValidAccessKey(access_key)) {
      console.warn('Invalid access key format, skipping fetch');
      return;
    }

    try {
      const profile = userProfileRef.current;
      const raw = await validateAccess(access_key, {
        fieldEngineerId: profile?.field_engineer_id,
      });
      const accessData = normalizeAssetForAccessKey(access_key, raw);
      sweepCompletedTaskStorage(access_key, accessData);

      const accesses = profile.accesses || {};
      const accessHistory = profile.access_history || [];

      let existingAccessKey = null;
      for (const key in accesses) {
        if (accesses[key].id === accessData.id) {
          existingAccessKey = key;
          break;
        }
      }

      const getExpirationTime = (access) => {
        if (!access?.access_duration_until) return Infinity;
        return new Date(access.access_duration_until).getTime();
      };

      if (existingAccessKey) {
        const existingAccess = accesses[existingAccessKey];
        if (
          getExpirationTime(accessData) > getExpirationTime(existingAccess)
        ) {
          const { [existingAccessKey]: removedAccess, ...remainingAccesses } = accesses;
          updateUserProfile({
            accesses: {
              ...remainingAccesses,
              [access_key]: slimAccessForPersist(accessData, access_key),
            },
            access_history: appendAccessHistory(accessHistory, removedAccess),
          });
        }
      } else {
        updateUserProfile({
          accesses: {
            ...accesses,
            [access_key]: slimAccessForPersist(accessData, access_key),
          },
        });
      }

      setAssetMap((prev) => ({
        ...prev,
        [access_key]: { data: accessData, lastFetched: Date.now() },
      }));
    } catch (err) {
      if (err.status === 403 || err.status === 404) {
        console.warn('Access forbidden or not found; moving to access_history.');
        const profile = userProfileRef.current;
        const accesses = profile.accesses || {};
        const accessHistory = profile.access_history || [];
        const { [access_key]: removedAccess, ...remainingAccesses } = accesses;
        updateUserProfile({
          accesses: remainingAccesses,
          access_history: appendAccessHistory(accessHistory, removedAccess),
        });
        return;
      }
      console.error('Error fetching and validating access:', err);
    }
  }, [updateUserProfile]);

  const fetchAssetDetails = useCallback(async (access_key) => {
    if (!isValidAccessKey(access_key)) {
      console.warn('Invalid access key format, skipping fetch');
      return;
    }

    try {
      const profile = userProfileRef.current;
      const raw = await fetchAssetByAccessKey(access_key, {
        fieldEngineerId: profile?.field_engineer_id,
      });
      const data = normalizeAssetForAccessKey(access_key, raw);
      sweepCompletedTaskStorage(access_key, data);
      setAssetMap((prev) => ({
        ...prev,
        [access_key]: { data, lastFetched: Date.now() },
      }));
    } catch (err) {
      if (err.status === 403 || err.status === 404) {
        console.warn('Access forbidden or not found; moving to access_history.');
        const profile = userProfileRef.current;
        const accesses = profile.accesses || {};
        const accessHistory = profile.access_history || [];
        const { [access_key]: removedAccess, ...remainingAccesses } = accesses;
        updateUserProfile({
          accesses: remainingAccesses,
          access_history: appendAccessHistory(accessHistory, removedAccess),
        });
        return;
      }
      console.error('Error fetching asset:', err);
    }
  }, [updateUserProfile]);

  useEffect(() => {
    if (!userProfile.accesses || Object.keys(userProfile.accesses).length === 0) return undefined;
    let cancelled = false;
    setError(null);

    const now = Date.now();
    const validAccessKeys = Object.keys(userProfile.accesses).filter((key) => {
      if (!isValidAccessKey(key)) {
        console.warn('Skipping invalid access key in fetch loop');
        return false;
      }
      return true;
    });

    const needsNetworkFetch = validAccessKeys.some((access_key) => {
      const existing = assetMapRef.current[access_key];
      return !existing || now - existing.lastFetched > FIVE_MINUTES;
    });

    if (!needsNetworkFetch) {
      return undefined;
    }

    setLoading(true);

    const fetchPromises = validAccessKeys.map(async (access_key) => {
      if (cancelled) return;

      const existing = assetMapRef.current[access_key];
      if (existing && now - existing.lastFetched <= FIVE_MINUTES) {
        return;
      }

      try {
        const profile = userProfileRef.current;
        const feId = profile?.field_engineer_id;
        const query = feId ? `?field_engineer_id=${encodeURIComponent(feId)}` : '';
        const url = `${API_URL}/v1/assets/access_key/${access_key}.json${query}`;
        const res = await fetch(url);
        if (cancelled) return;

        if (!res.ok) {
          if (res.status === 403 || res.status === 404) {
            console.warn('Access forbidden or not found; moving to access_history.');
            const profile = userProfileRef.current;
            const accesses = profile.accesses || {};
            const accessHistory = profile.access_history || [];
            const { [access_key]: removedAccess, ...remainingAccesses } = accesses;
            updateUserProfile({
              accesses: remainingAccesses,
              access_history: appendAccessHistory(accessHistory, removedAccess),
            });
            return;
          }
          throw new Error(`Fetch error: ${res.status} ${res.statusText}`);
        }
        const raw = await res.json();
        if (cancelled) return;

        const assetData = normalizeAssetForAccessKey(access_key, raw);
        sweepCompletedTaskStorage(access_key, assetData);

        setAssetMap((prev) => ({
          ...prev,
          [access_key]: { data: assetData, lastFetched: Date.now() },
        }));
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching asset:', err);
        }
      }
    });

    Promise.all(fetchPromises)
      .then(() => {
        if (!cancelled) setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userProfile.accesses, updateUserProfile]);

  const assets = Object.keys(userProfile.accesses || {}).map((access_key) => {
    const entry = assetMap[access_key];
    return {
      access_key,
      ...userProfile.accesses[access_key],
      ...(entry ? entry.data : {}),
    };
  });

  const refreshAll = useCallback(() => {
    if (!userProfile.accesses) return;
    Object.keys(userProfile.accesses).forEach((access_key) => {
      fetchAssetDetails(access_key);
    });
  }, [userProfile.accesses, fetchAssetDetails]);

  return (
    <AccessContext.Provider value={{ assetMap, assets, loading, error, refreshAll, fetchAndValidateAccess, fetchAssetDetails }}>
      {children}
    </AccessContext.Provider>
  );
}

export function userHasAccess(userProfile, access_key) {
  return userProfile.accesses && userProfile.accesses[access_key];
}
